import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  resendRamNamBookOtpApi,
  verifyLoginRamNamOtpApi,
  verifyRamNamOtpApi,
} from "./RamNamBookCRUD";
import LoginPage from "../auth-pages/LoginPage";
import { FaArrowLeft } from "react-icons/fa";
import MemberHeader from "./member-dasboard/MemberHeader";

const OTP_LENGTH = 4;
const RESEND_TIME = 300;

const OTPVerifyPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [uiOTP, setUiOTP] = useState("");
  const [timer, setTimer] = useState(RESEND_TIME);
  const [canResend, setCanResend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const isLoggedIn = !!localStorage.getItem("customer");

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/member/dashboard", { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!state?.mobileNumber || !state?.mode) {
      navigate("/");
    }
  }, [state, navigate]);

  /* ⏱ OTP Timer */
  useEffect(() => {
    if (timer <= 0) {
      setCanResend(true);
      return;
    }

    const interval = setInterval(() => {
      setTimer((t) => t - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  /* 📩 Set OTP on first load */
  useEffect(() => {
    if (state?.otpdata) {
      resetTimer();
      setUiOTP(state.otpdata);
    }
  }, [state?.otpdata]);

  /* 🔁 Helpers */
  const resetTimer = () => {
    setTimer(RESEND_TIME);
    setCanResend(false);
  };

  const resetOtpInputs = () => {
    setOtp(Array(OTP_LENGTH).fill(""));
    document.getElementById("otp-0")?.focus();
  };

  /* 🔢 OTP Input Change */
  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);

    if (value && index < OTP_LENGTH - 1) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  /* ✅ Verify OTP */
  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== OTP_LENGTH) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);

    try {
      const response =
        state.mode === "registration"
          ? await verifyRamNamOtpApi(state.mobileNumber, enteredOtp)
          : await verifyLoginRamNamOtpApi(state.mobileNumber, enteredOtp);

      if (response.data.isSuccess) {
        toast.success(response.data.message);
        // localStorage.setItem("customer", JSON.stringify(response.data.data));

        const customerData = {
          ramNamBookMemberID: response.data.data.ramNamBookMemberID,
          memberName: response.data.data.memberName, // 👈 customer name stored
          mobileNumber: response.data.data.mobileNumber,
          addressAndText: response.data.data.addressAndText,
          villageCityName: response.data.data.villageCityName,
          pincode: response.data.data.pincode,
          countryName: response.data.data.countryName,
        };

        localStorage.setItem("customer", JSON.stringify(customerData));

        // ✅ Store OTP verified time (for checkout protection)
        sessionStorage.setItem("otpVerifiedAt", Date.now().toString());

        resetOtpInputs();
        resetOtpInputs();
        // navigate("/page/store");
        navigate("/member/dashboard");
      } else {
        toast.error(response.data.message);
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* 🔁 Resend OTP */
  const handleResendOtp = async () => {
    if (!canResend) return;

    try {
      const res = await resendRamNamBookOtpApi(state.mobileNumber);

      if (res.data.isSuccess) {
        toast.success(res.data.message);
        setUiOTP(res.data.data || "");
        resetOtpInputs();
        resetTimer();
      } else {
        toast.error(res.data.message);
        setCanResend(true);
      }
    } catch {
      toast.error("Failed to resend OTP. Try again.");
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-6 col-md-8 col-12">
          <div className="card shadow border-0 rounded-4">
            <div className="card-header bg-white border-0 d-flex justify-content-between">
              <button
                className="btn btn-sm btn-link text-decoration-none"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft />
              </button>
            </div>

            <div className="card-body p-4 p-md-5 text-center">
              <h6 className="fw-bold mb-3" style={{ color: "#FF4F00" }}>
                Mobile Number Verification
              </h6>

              <p className="text-muted mb-4">
                We have sent an OTP to this {state.mobileNumber} number
              </p>

              {/* OTP Inputs */}
              <div className="d-flex justify-content-center gap-2 mb-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    className="form-control text-center fw-bold"
                    style={{ width: 45, height: 45, fontSize: 18 }}
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target.value, index)}
                  />
                ))}
              </div>

              <h6 className="mb-3">
                {state.mode === "registration"
                  ? "Verify OTP to Register"
                  : "Verify OTP to Login"}
              </h6>

              <button
                className="btn px-5 mb-3"
                style={{ backgroundColor: "#FF4F00", color: "#fff" }}
                onClick={handleVerifyOtp}
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </button>

              {/* Resend */}
              <div>
                <small className="text-muted d-block mb-1">
                  Didn’t receive OTP?
                </small>
                <button
                  className="btn btn-link p-0"
                  style={{ color: "#FF4F00" }}
                  onClick={handleResendOtp}
                  disabled={!canResend}
                >
                  {canResend ? "Resend OTP" : `Resend OTP in ${timer}s`}
                </button>
              </div>

              {/* Testing OTP */}
              {uiOTP && (
                <div className="mt-3 small text-muted">
                  (Test OTP: <b>{uiOTP}</b>)
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* <MemberHeader custome={state.memberName} /> */}

      <LoginPage show={showLogin} onHide={() => setShowLogin(false)} />
    </div>
  );
};

export default OTPVerifyPage;
